import {Component, Prop, Vue} from 'vue-facing-decorator';
import {type RouteLocationRaw} from 'vue-router';
import AppPopup from '../../../components/AppPopup.vue';
import {errorHandler} from '../../../utils/portuna';
import dateHelper, {parseDate} from '../../../utils/portuna/dateHelper';
import {getDateInformation, incrementDate, parseDateRange, setToMonday} from '../../date';
import {Popup, popupDefaultCancelButton} from '../../popup';
import {strtotime} from '../../strtotime';
import {MODE_ABSENCES, MODE_MANUAL} from './time-tracker';

@Component
export default class AEvaluationTableComponent extends Vue
{
    @Prop({
        type: Number,
        required: true,
    }) employee: number;

    @Prop({
        type: String,
        required: true,
    }) week: string;

    dateInformation = {} as DateInformation;

    hasChanges = false;

    today = dateHelper('Y-m-d') as string;

    // overwrite in component
    startDate: string = '';

    // overwrite in component
    workStart: string = '';

    employeeEnabledSystems: number[] = [];

    settings: Nullable<ITimeTrackerSettingsDateData> = null;

    get timeTrackerMode()
    {
        return this.$store.state.layoutData.employee_configuration!.time_tracker_mode;
    }

    get hasAbsences()
    {
        return MODE_ABSENCES & this.timeTrackerMode;
    }

    get isManual()
    {
        return MODE_MANUAL & this.timeTrackerMode;
    }

    get todayLinkParams(): RouteLocationRaw
    {
        const today = new Date(dateHelper('Y-m-d') as string);
        setToMonday(today);

        if (!this.checkStartDate(today))
        {
            return '';
        }

        return {
            name: this.$route.name as string,
            params: {
                employee: String(this.employee),
                week: dateHelper('Y-m-d', today) as string,
            },
        };
    }

    get nextLinkParams(): RouteLocationRaw
    {
        const monday = this.getDateByStrToTime('+7 days');

        if (!this.checkStartDate(monday))
        {
            return '';
        }

        return {
            name: this.$route.name as string,
            params: {
                employee: String(this.employee),
                week: monday,
            },
        };
    }

    get previousLinkParams(): RouteLocationRaw
    {
        const monday = this.getDateByStrToTime('-7 days');

        if (!this.checkStartDate(monday))
        {
            return '';
        }

        return {
            name: this.$route.name as string,
            params: {
                employee: String(this.employee),
                week: monday,
            },
        };
    }

    get range(): string
    {
        let end = parseDate(this.dateInformation.date);
        if (end instanceof Date)
        {
            end.setDate(end.getDate() + 6);
        }

        return parseDateRange(this.dateInformation.date, dateHelper('Y-m-d', end) as string);
    }

    get screenReaderRange(): string
    {
        let end = parseDate(this.dateInformation.date);
        if (end instanceof Date)
        {
            end.setDate(end.getDate() + 6);
        }

        return parseDateRange(this.dateInformation.date, dateHelper('Y-m-d', end) as string, true);
    }

    get currentWeek()
    {
        const date = new Date(this.today);
        setToMonday(date);

        return dateHelper('Y-m-d', date) as string;
    }

    get isToday()
    {
        const currentDateInformation = getDateInformation(this.week);
        const todayDateInformation   = getDateInformation(this.today);

        // same date
        return todayDateInformation.year === currentDateInformation.year && todayDateInformation.week === currentDateInformation.week;
    }

    checkStartDate(monday: Date | string): boolean
    {
        if (this.currentUser.isSupport)
        {
            return true;
        }

        const sundayDate = new Date(monday);
        incrementDate(sundayDate, 6);
        const sunday = dateHelper('Y-m-d', sundayDate);

        if (!this.isManual && (!this.startDate || sunday < this.startDate))
        {
            return false;
        }

        return !!(this.workStart && sunday > this.workStart);
    }

    getDateByStrToTime(string: string)
    {
        return dateHelper('Y-m-d', strtotime(string, new Date(this.dateInformation.date)) * 1000) as string;
    }

    goToEmployee(employeeId: number | string)
    {
        if ('number' === typeof employeeId)
        {
            employeeId = employeeId.toString();
        }

        if (employeeId === '' + this.$route.params.id)
        {
            return;
        }

        const location: RouteLocationRaw = {
            name: this.$route.name as string,
            params: {
                employee: employeeId,
                week: this.week,
            },
        };

        if (!this.hasChanges)
        {
            this.$router.push(location).catch(errorHandler);
            return;
        }

        this.discardChangesPopup('employee', location);
    }

    goToWeek(location: RouteLocationRaw)
    {
        if (!this.hasChanges)
        {
            this.$router.push(location).catch(errorHandler);
            return;
        }

        this.discardChangesPopup('week', location);
    }

    discardChangesPopup(action: 'week' | 'employee', location: RouteLocationRaw)
    {
        const popup = new Popup({
            title: 'Änderungen verwerfen',
            titleIcon: 'delete',
            text: `In der aktuellen Woche existieren Änderungen. Wenn Sie ${action === 'week'
                ? 'die Woche'
                : 'den Mitarbeiter'} wechseln, werden die Änderungen verworfen. Wollen Sie ${action === 'week'
                ? 'die Woche'
                : 'den Mitarbeiter'} wechseln und die Änderungen verwerfen?`,
            textIcon: 'delete',
            textIconClass: 'text-danger',
            size: 'small',
            buttons: [
                Object.assign({}, popupDefaultCancelButton, {
                    cmd: (popup: typeof AppPopup, event: MouseEvent) =>
                    {
                        // @ts-ignore tmp build fix
                        popup.close(event);
                    },
                }),
                {
                    type: 'danger',
                    title: 'Änderungen verwerfen',
                    icon: 'delete',
                    cmd: popup =>
                    {
                        popup.close();
                        this.$router.push(location).catch(errorHandler);
                    },
                },
            ],
        });

        popup.open();
    }
}